import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Phone, FileText, Sparkles, Send } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const RATE_LIMIT_KEY = 'b2b_sales_last_submit';
const RATE_LIMIT_HOURS = 1;

export default function B2BSales() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gender: '',
    age: '',
    expectedSalary: '',
    cvUrl: '',
    botField: '',
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const isRateLimited = () => {
    const lastSubmit = localStorage.getItem(RATE_LIMIT_KEY);
    if (!lastSubmit) return false;
    const hoursSinceLast = (Date.now() - parseInt(lastSubmit)) / (1000 * 60 * 60);
    return hoursSinceLast < RATE_LIMIT_HOURS;
  };

  const validatePhone = (value) => {
    const cleanPhone = value.replace(/[+\s\-\(\)\.]/g, '');
    if (cleanPhone.length < 10 || cleanPhone.length > 15) {
      return t('b2bSales.phoneError', 'Please enter a valid phone number');
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'phone') {
      setPhoneError(validatePhone(value));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      gender: '',
      age: '',
      expectedSalary: '',
      cvUrl: '',
      botField: '',
    });
    setErrorMessage('');
    setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (formData.botField) {
      setStatus('success');
      return;
    }

    if (isRateLimited()) {
      setStatus('error');
      setErrorMessage(t('b2bSales.rateLimit', 'You can only submit once per hour. Please try again later.'));
      return;
    }

    const phoneErr = validatePhone(formData.phone);
    if (phoneErr) {
      setPhoneError(phoneErr);
      setStatus('error');
      setErrorMessage(phoneErr);
      return;
    }

    if (!formData.cvUrl.trim()) {
      setStatus('error');
      setErrorMessage(t('b2bSales.cvError', 'Please provide a CV link'));
      return;
    }

    setStatus('loading');

    try {
      const submissionData = new FormData();
      submissionData.append('name', formData.name);
      submissionData.append('phone', formData.phone);
      submissionData.append('gender', formData.gender);
      submissionData.append('age', formData.age);
      submissionData.append('expectedSalary', formData.expectedSalary);
      submissionData.append('cvUrl', formData.cvUrl);

      const webhookUrl = import.meta.env.VITE_B2B_SALES_WEBHOOK_URL || 'https://n8n.nodatx.com/webhook-test/b2b-sales';

      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: submissionData,
      });

      if (!response.ok) {
        throw new Error(t('b2bSales.apiError', 'Failed to submit. Please try again.'));
      }

      localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());

      setStatus('success');
      resetForm();
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || t('b2bSales.genericError', 'An unexpected error occurred. Please try again.'));
    }
  };

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 overflow-hidden font-sans">
      <SEOHead
        title={t('b2bSales.title', 'B2B Sales Position - Apply Now | Nodatix')}
        description={t('b2bSales.description', 'Join our B2B Sales team at Nodatix. Submit your application with your CV/resume.')}
        canonical="https://nodatx.com/b2b-sales"
      />
      
      {/* Purple Background - matching Hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#0a0a0f]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse-slow"></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      <div className="relative z-10 w-full max-w-md mx-auto">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">
            <span className="text-purple-400">{t('b2bSales.heading2', 'B2B Sales')}</span> {t('b2bSales.heading1', 'Application')}
          </h1>
          <p className="mt-2 text-gray-400 text-sm sm:text-base">
            {t('b2bSales.subtitle', "Submit your CV and we'll be in touch!")}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#0f0f1a]/95 backdrop-blur-2xl border border-purple-500/20 rounded-3xl p-6 sm:p-8 shadow-[0_0_100px_-30px_rgba(139,92,246,0.25)] relative overflow-hidden">
          {/* Inner glow */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-[60px]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:1.5rem_1.5rem]"></div>
          
          <div className="relative z-10 w-full min-h-[400px] flex flex-col justify-center transition-all duration-500">
            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="mx-auto flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-[0_0_40px_rgba(139,92,246,0.4)] mb-6 sm:mb-8 drop-shadow-2xl relative">
                  <div className="absolute inset-0 bg-purple-400 rounded-2xl animate-ping opacity-20"></div>
                  <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-white animate-pulse" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  {t('b2bSales.successHeading', 'Application Received!')}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-xs mx-auto">
                  {t('b2bSales.successMsg', "We have received your CV and will be in touch soon. Thank you for your interest in joining our B2B Sales team!")}
                </p>
                <button
                  onClick={() => {
                    setStatus('idle');
                    resetForm();
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 active:scale-95 transition-all font-medium text-sm cursor-pointer"
                >
                  {t('b2bSales.successBtn', 'Submit Another Application')}
                </button>
              </div>
            ) : (
              <form 
                className={`space-y-5 transition-opacity duration-300 ${status === 'loading' ? 'opacity-80 pointer-events-none' : 'opacity-100'}`} 
                onSubmit={handleSubmit}
              >
                {/* Honeypot */}
                <div className="opacity-0 absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="botField">{t('b2bSales.honeypot', "Don't fill this if you're human")}</label>
                  <input
                    type="text"
                    name="botField"
                    id="botField"
                    value={formData.botField}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.fullName', 'Full Name')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-focus-within:text-purple-400 transition-colors">
                        <User className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="block w-full pl-10 sm:pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all hover:bg-white/[0.07] text-sm sm:text-base"
                        placeholder={t('b2bSales.namePlaceholder', 'Enter your full name')}
                      />
                    </div>
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.phone', 'Phone Number')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-focus-within:text-purple-400 transition-colors">
                        <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className={`block w-full pl-10 sm:pl-12 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 transition-all hover:bg-white/[0.07] text-sm sm:text-base ${phoneError ? 'border-red-500 focus:ring-red-500' : 'border-white/10 focus:ring-purple-500/50 focus:border-purple-500/50'}`}
                        placeholder={t('b2bSales.phonePlaceholder', '+1 234 567 8900')}
                      />
                    </div>
                    {phoneError && (
                      <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {phoneError}
                      </p>
                    )}
                  </div>

                  {/* Gender Field */}
                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.gender', 'Gender')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <select
                        id="gender"
                        name="gender"
                        required
                        value={formData.gender}
                        onChange={handleChange}
                        className="appearance-none block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all hover:bg-white/[0.07] text-sm sm:text-base cursor-pointer"
                      >
                        <option value="" disabled className="bg-[#0f0f1a] text-gray-600">{t('b2bSales.selectGender', 'Select gender')}</option>
                        <option value="Male" className="bg-[#0f0f1a] text-white">{t('b2bSales.male', 'Male')}</option>
                        <option value="Female" className="bg-[#0f0f1a] text-white">{t('b2bSales.female', 'Female')}</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </div>

                  {/* Age Field */}
                  <div>
                    <label htmlFor="age" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.age', 'Age')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <select
                        id="age"
                        name="age"
                        required
                        value={formData.age}
                        onChange={handleChange}
                        className="appearance-none block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all hover:bg-white/[0.07] text-sm sm:text-base cursor-pointer"
                      >
                        <option value="" disabled className="bg-[#0f0f1a] text-gray-600">{t('b2bSales.selectAge', 'Select age')}</option>
                        <option value="18-24" className="bg-[#0f0f1a] text-white">{t('b2bSales.age18_24', '18-24')}</option>
                        <option value="25-34" className="bg-[#0f0f1a] text-white">{t('b2bSales.age25_34', '25-34')}</option>
                        <option value="35-44" className="bg-[#0f0f1a] text-white">{t('b2bSales.age35_44', '35-44')}</option>
                        <option value="45-54" className="bg-[#0f0f1a] text-white">{t('b2bSales.age45_54', '45-54')}</option>
                        <option value="55+" className="bg-[#0f0f1a] text-white">{t('b2bSales.age55', '55+')}</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </div>

                  {/* Expected Salary Field */}
                  <div>
                    <label htmlFor="expectedSalary" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.salary', 'Expected Salary')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <select
                        id="expectedSalary"
                        name="expectedSalary"
                        required
                        value={formData.expectedSalary}
                        onChange={handleChange}
                        className="appearance-none block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all hover:bg-white/[0.07] text-sm sm:text-base cursor-pointer"
                      >
                        <option value="" disabled className="bg-[#0f0f1a] text-gray-600">{t('b2bSales.selectSalary', 'Select expected salary')}</option>
                        <option value="EGP 5,000 - 10,000" className="bg-[#0f0f1a] text-white">{t('b2bSales.salary5000', 'EGP 5,000 - 10,000')}</option>
                        <option value="EGP 10,000 - 15,000" className="bg-[#0f0f1a] text-white">{t('b2bSales.salary10000', 'EGP 10,000 - 15,000')}</option>
                        <option value="EGP 15,000 - 20,000" className="bg-[#0f0f1a] text-white">{t('b2bSales.salary15000', 'EGP 15,000 - 20,000')}</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </div>

                  {/* CV Link */}
                  <div>
                    <label htmlFor="cvUrl" className="block text-sm font-medium text-gray-300 mb-1.5">
                      {t('b2bSales.cvLink', 'CV / Resume Link')} <span className="text-purple-400">*</span>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-500 group-focus-within:text-purple-400 transition-colors">
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <input
                        id="cvUrl"
                        name="cvUrl"
                        type="url"
                        required
                        value={formData.cvUrl}
                        onChange={handleChange}
                        className="block w-full pl-10 sm:pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all hover:bg-white/[0.07] text-sm sm:text-base"
                        placeholder={t('b2bSales.cvPlaceholder', 'https://drive.google.com/... or LinkedIn profile')}
                      />
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {status === 'error' && errorMessage && (
                  <div className="rounded-xl bg-red-500/10 p-3 sm:p-4 border border-red-500/20 flex items-start">
                    <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="ml-3 text-sm text-red-300">{errorMessage}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group relative w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-purple-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] active:scale-[0.98] cursor-pointer disabled:opacity-90 disabled:hover:scale-100 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
                  
                  {status === 'loading' ? (
                    <div className="flex items-center gap-2 relative z-10">
                      <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('b2bSales.submitting', 'Submitting...')}</span>
                    </div>
                  ) : (
                    <>
                      <span className="relative z-10">{t('b2bSales.submit', 'Submit Application')}</span>
                      <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer text */}
        <p className="mt-4 text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
          {t('b2bSales.secure', 'Your data is secure and never shared')}
        </p>
      </div>
    </section>
  );
}
